import { LinkRelationship } from '../../enums/link.relationship';
import { EntityLink } from '../entity';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { QuotePro, QuoteSimpleStatus } from './quote.pro';

/**
 * Create/update allowlist (production contract).
 * Number, status, version, lifecycle dates, decision facts, and audit fields are server-owned.
 * Totals and prepayment totals are derived by calculateQuote.
 */
export const QUOTE_MUTABLE_FIELDS = [
  'account',
  'site',
  'contact',
  'company',
  'owner',
  'date',
  'expiryDate',
  'policy',
  'costSource',
  'pricingMethod',
  'multiplier',
  'priceList',
  'budget',
  'description',
  'terms',
  'note',
  'theme',
  'sections',
  'attachments',
  'jobType',
  'customerQO',
  'customerRef',
  'name',
  'tags',
  'quoteName',
  'address',
  'type',
  'template',
  'links',
  'overridePrepaymentEx',
  'overridePrepaymentInc',
  'overridePrepaymentTax',
] as const;

export type QuoteMutableField = (typeof QUOTE_MUTABLE_FIELDS)[number];

/** Fields clients must never set via generic save/patch. */
export const QUOTE_PROTECTED_FIELDS = [
  'quoteNumber',
  'status',
  'version',
  'approvalDate',
  'acceptedDate',
  'sentDate',
  'approver',
  'acceptor',
  'signature',
  'poNumber',
  'poAttachments',
  'changesRequiredNote',
  'reasonForCancellation',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'active',
  'pdf',
  'isExpired',
  'totalBuy',
  'totalSellEx',
  'totalAdjustment',
  'totalTax',
  'totalSellInc',
  'prepaymentEx',
  'prepaymentTax',
  'prepaymentInc',
] as const;

export type QuoteProtectedField = (typeof QUOTE_PROTECTED_FIELDS)[number];

export function listForbiddenQuotePatchFields(patch: Partial<QuotePro> | Record<string, unknown> | null | undefined): string[] {

  if (!patch) return [];
  return QUOTE_PROTECTED_FIELDS.filter(field => Object.prototype.hasOwnProperty.call(patch, field));
}

export function assertQuotePatchAllowlist(patch: Partial<QuotePro> | Record<string, unknown> | null | undefined): void {

  const forbidden = listForbiddenQuotePatchFields(patch);
  if (forbidden.length > 0) {
    throw new Error(`Quote patch rejected protected fields: ${forbidden.join(', ')}`);
  }
}

/** Date-typed fields accepted from clients. Wire format is an ISO string (or Date/null). */
export const QUOTE_DATE_FIELDS = ['date', 'expiryDate'] as const;

/**
 * Coerce ISO strings/numbers to Dates and reject anything uncastable (e.g. a Date instance
 * degraded to `{}` by spread-cloning) so it fails loudly instead of corrupting the write.
 */
export function normalizeQuoteDates(patch: Partial<QuotePro> | null | undefined): Partial<QuotePro> {

  const result = { ...(patch ?? {}) } as Record<string, unknown>;
  for (const field of QUOTE_DATE_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(result, field)) continue;
    const value = result[field];
    if (value === null || value === undefined) continue;
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) throw new Error(`${field} is not a valid date`);
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) throw new Error(`${field} is not a valid date`);
      result[field] = parsed;
      continue;
    }
    throw new Error(`${field} is not a valid date value`);
  }
  return result as Partial<QuotePro>;
}

export function pickQuoteMutableFields(source: Partial<QuotePro> | null | undefined): Partial<QuotePro> {

  const result: Partial<QuotePro> = {};
  if (!source) return result;

  for (const field of QUOTE_MUTABLE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      (result as Record<string, unknown>)[field] = (source as Record<string, unknown>)[field];
    }
  }
  return normalizeQuoteDates(result);
}

/** Draft and Changes Required are editable. Pending Approval is locked for the approval queue. */
export function isQuoteEditable(status: QuoteSimpleStatus | undefined | null): boolean {

  return status == null
    || status === QuoteSimpleStatus.Draft
    || status === QuoteSimpleStatus.ChangesRequired;
}

/**
 * A Quote Template's whole lifecycle: Draft, then Approved (shown as Public, which offers it on the
 * quote's template picker). Legacy's template editor kept both writable, so neither locks.
 */
export function isQuoteTemplateEditable(status: QuoteSimpleStatus | undefined | null): boolean {

  return status == null
    || status === QuoteSimpleStatus.Draft
    || status === QuoteSimpleStatus.Approved;
}

export function assertQuoteEditable(status: QuoteSimpleStatus | undefined | null): void {

  if (!isQuoteEditable(status)) {
    throw new Error(`Quote is not editable in status ${status}`);
  }
}

/** Draft disposal is Delete, not Cancel. */
export function canDeleteQuote(status: QuoteSimpleStatus | undefined | null): boolean {

  return status == null || status === QuoteSimpleStatus.Draft;
}

export function assertQuoteDeletable(status: QuoteSimpleStatus | undefined | null): void {

  if (!canDeleteQuote(status)) {
    throw new Error(`Quotes can only be deleted in Draft; current status is ${status}`);
  }
}

/** Prepayment Invoice and converted-Job lineage stay server-owned on generic save/patch. */
export function sanitizeQuoteClientLinks(links: EntityLink[] | undefined, existing?: EntityLink[]): EntityLink[] {

  const preserved = (existing ?? []).filter(isServerOwnedQuoteLink);
  const clientLinks = (links ?? []).filter(link => !isServerOwnedQuoteLink(link));
  return [...clientLinks, ...preserved];
}

export function isServerOwnedQuoteLink(link: EntityLink | null | undefined): boolean {

  if (!link?.type) return false;
  if (link.type === ActivityEntityName.PrepaymentInvoice) return true;
  return link.type === ActivityEntityName.Job && link.relationship === LinkRelationship.To;
}

export function findPrepaymentInvoiceLink(quote: Pick<QuotePro, 'links'> | null | undefined): EntityLink | undefined {

  return (quote?.links ?? []).find(link => link?.type === ActivityEntityName.PrepaymentInvoice);
}
